/**
 * Natura IVA form logic, shared by the invoice and autofattura editors.
 *
 * "Natura" is the code that explains *why* a line carries no VAT. FatturaPA
 * admits it only on a zero-rated line and requires it there — a 0% line sent
 * without one comes back from SdI as a scarto — so the rules that decide when
 * to show and when to demand the field live here rather than in each form.
 *
 * Kept free of Svelte imports so it can be unit-tested directly.
 */

// Official FatturaPA v1.2 code list. The bare N2/N3/N6 were retired in v1.2.1
// in favour of their sub-codes and must not be offered.
export const NATURA_CODES = [
  'N1', 'N2.1', 'N2.2', 'N3.1', 'N3.2', 'N3.3', 'N3.4', 'N3.5', 'N3.6',
  'N4', 'N5', 'N6.1', 'N6.2', 'N6.3', 'N6.4', 'N6.5', 'N6.6', 'N6.7',
  'N6.8', 'N6.9', 'N7',
];

/**
 * True when a VAT rate obliges the line to declare a Natura.
 * Blank/unparseable input reads as "not yet zero", so the select stays hidden
 * while the operator is still typing.
 */
export function requiresNatura(aliquotaIva) {
  const rate = parseFloat(aliquotaIva);
  return Number.isFinite(rate) && rate === 0;
}

/**
 * 1-based numbers of the filled-in lines that are zero-rated but carry no
 * Natura. Empty array means the document is safe to submit.
 * Rows the operator has not described yet are skipped: they are dropped before
 * the payload is built, so flagging them would be noise.
 */
export function missingNaturaLines(lineItems) {
  if (!Array.isArray(lineItems)) return [];
  return lineItems.reduce((acc, item, i) => {
    if (item?.descrizione && requiresNatura(item.aliquotaIva) && !item.natura) {
      acc.push(i + 1);
    }
    return acc;
  }, []);
}

/**
 * Suggested Natura for a document type, or '' when there is no sensible one.
 *
 * docs/autofatture.md prescribes Italian VAT (typically 22%) on TD17/TD18, so
 * these are only ever a prefill for the operator who deliberately zero-rates a
 * reverse-charge line — never an automatic choice, and always editable.
 */
const DEFAULT_NATURA_BY_TIPO = {
  TD17: 'N6.9',
  TD18: 'N6.9',
};

export function defaultNaturaFor(tipoDocumento) {
  return DEFAULT_NATURA_BY_TIPO[tipoDocumento] || '';
}
